import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Per-route <head> management for the SPA.
 *
 * WHY: every /app/* route used to ship the identical index.html: one shared
 * title, no canonical, no robots meta. To Googlebot the ~600 discovered
 * /app/players/sl##### URLs were byte-identical duplicates, so GSC filled
 * with "Duplicate without user-selected canonical" and Google picked
 * arbitrary winners (sl13423 canonicalized to sl13411). Report: 38 pages,
 * 2026-08-08 coverage export.
 *
 * Googlebot renders JS, so directives injected here are honored:
 * - Free research routes: unique title + self-canonical, indexable. (The
 *   long-term fix for these is prerendering; the crawlable content today
 *   lives on /draft-rankings/ and /rookie-rankings/.)
 * - Everything else (player shells, gated tools, auth/account plumbing):
 *   noindex,follow. To a crawler these render nothing useful, and an
 *   explicit directive beats letting Google guess at duplicates.
 *
 * Titles: computed season year, never typed (standing rule; pages shipped
 * "2025-26" deep into the 2026 draft season before this was enforced).
 */

const now = new Date();
const SEASON_YEAR = now.getMonth() + 1 >= 2 ? now.getFullYear() : now.getFullYear() - 1;

const ORIGIN = 'https://fantasy.draftedge.com';
const BASE = '/app';

type Rule = { title: string; index: boolean; canonical?: string };

/** First match wins; longest prefixes first. */
const RULES: Array<[RegExp, Rule]> = [
  // Cross-canonical: these two routes carry the same rankings the
  // server-rendered SEO pages publish, and two indexable copies of one
  // dataset cannibalize each other. The static pages are the ones with
  // crawlable content, so they get the equity.
  [/^\/rankings/, { title: `${SEASON_YEAR} Fantasy Football Rankings | DraftEdge App`, index: true, canonical: `${ORIGIN}/draft-rankings/` }],
  [/^\/rookies/, { title: `${SEASON_YEAR} Rookie Rankings | DraftEdge App`, index: true, canonical: `${ORIGIN}/rookie-rankings/` }],
  [/^\/trending/, { title: 'Waiver Wire Trends | DraftEdge App', index: true }],
  [/^\/depth-charts/, { title: 'NFL Depth Charts | DraftEdge App', index: true }],
  [/^\/injuries/, { title: 'NFL Injury Report | DraftEdge App', index: true }],
  [/^\/news\/.+/, { title: 'NFL News | DraftEdge App', index: false }],
  [/^\/news/, { title: 'NFL News | DraftEdge App', index: true }],
  [/^\/players\//, { title: 'Player Profile | DraftEdge App', index: false }],
  [/^\/drafts/, { title: 'Draft Room | DraftEdge App', index: false }],
  [/^\/cheatsheet/, { title: 'Cheat Sheets | DraftEdge App', index: false }],
  [/^\/mocks/, { title: 'Mock Drafts | DraftEdge App', index: false }],
  [/^\/compare/, { title: 'Compare Players | DraftEdge App', index: false }],
  [/^\/exposure/, { title: 'Exposure Report | DraftEdge App', index: false }],
  [/^\/dashboard/, { title: 'Dashboard | DraftEdge App', index: false }],
  [/^\/login/, { title: 'Sign In | DraftEdge App', index: false }],
  [/^\/register/, { title: 'Create Account | DraftEdge App', index: false }],
  [/^\/$/, {
    title: `DraftEdge Draft Room: ${SEASON_YEAR} NFL Fantasy Draft Coach & Cheat Sheets`,
    index: true,
  }],
];

const FALLBACK: Rule = { title: 'DraftEdge App', index: false };

function upsert<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attr: string,
  value: string,
  marker: string
): HTMLElementTagNameMap[K] {
  let el = document.head.querySelector<HTMLElementTagNameMap[K]>(`${tag}[data-route-seo="${marker}"]`);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute('data-route-seo', marker);
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  return el;
}

function remove(marker: string) {
  document.head.querySelector(`[data-route-seo="${marker}"]`)?.remove();
}

export function RouteSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const rule = RULES.find(([re]) => re.test(pathname))?.[1] ?? FALLBACK;

    document.title = rule.title;

    if (rule.index) {
      remove('robots');
      const canon = upsert('link', 'rel', 'canonical', 'canonical');
      canon.setAttribute('rel', 'canonical');
      canon.setAttribute(
        'href',
        rule.canonical ?? ORIGIN + BASE + (pathname === '/' ? '/' : pathname)
      );
    } else {
      remove('canonical');
      const robots = upsert('meta', 'name', 'robots', 'robots');
      robots.setAttribute('name', 'robots');
      robots.setAttribute('content', 'noindex, follow');
    }
  }, [pathname]);

  return null;
}
